import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { usePermissions } from '@/hooks/usePermissions'
import type { UserRole } from '@/types'

interface ProtectedRouteProps {
  children: React.ReactNode
  permission?: string
  permissions?: string[]
  role?: UserRole
  roles?: UserRole[]
  requireAll?: boolean
  redirectTo?: string
}

/**
 * Componente para proteger rotas baseado em permissões e roles
 */
export function ProtectedRoute({
  children,
  permission,
  permissions,
  role,
  roles,
  requireAll = false,
  redirectTo = '/dashboard'
}: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuth()
  const { can, canAny, canAll, hasRole } = usePermissions()

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando permissões...</p>
        </div>
      </div>
    )
  }

  // Redirecionar para login se não autenticado
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Verificar permissões
  let hasPermission = true
  
  if (permission) {
    hasPermission = hasPermission && can(permission)
  }
  
  if (permissions && permissions.length > 0) {
    if (requireAll) {
      hasPermission = hasPermission && canAll(permissions)
    } else {
      hasPermission = hasPermission && canAny(permissions)
    }
  }
  
  // Verificar roles
  let hasRequiredRole = true
  
  if (role) {
    hasRequiredRole = hasRequiredRole && hasRole(role)
  }
  
  if (roles && roles.length > 0) {
    hasRequiredRole = hasRequiredRole && roles.some(r => hasRole(r))
  }
  
  const canAccess = hasPermission && hasRequiredRole
  
  // Se não tem acesso, redirecionar
  if (!canAccess) {
    return <Navigate to={redirectTo} replace />
  }
  
  return <>{children}</>
}

// Componentes de conveniência para casos comuns
export function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute role="administrador">
      {children}
    </ProtectedRoute>
  )
}

export function VistoriaRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute roles={['administrador', 'vistoria']}>
      {children}
    </ProtectedRoute>
  )
}

export function VistoriaPermissionRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute permission="vistoria_view">
      {children}
    </ProtectedRoute>
  )
}

export function UserManagementRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute permission="user_view">
      {children}
    </ProtectedRoute>
  )
}

export function CalendarManagementRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute permission="calendario_view">
      {children}
    </ProtectedRoute>
  )
}

export function MenuManagementRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute permission="cardapio_view">
      {children}
    </ProtectedRoute>
  )
}

export function GeolocationManagementRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute permission="geolocation_view">
      {children}
    </ProtectedRoute>
  )
}

export function ParametersRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute permission="parametro_view">
      {children}
    </ProtectedRoute>
  )
}

export function ReportsRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute permission="relatorio_view">
      {children}
    </ProtectedRoute>
  )
}

export function ConfigRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute permission="config_view">
      {children}
    </ProtectedRoute>
  )
}
